import { EstateType, Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { Box } from '@gemini/core';
import { Button, Card } from '@gemini/ui';
import Icon from '@mfe-presentation/components/Icon';
import useStepUtils from '@mfe-presentation/hooks/useStepUtils';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';
import { EActionType } from '@mfe-types/context';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';

interface Props {
    estateType: EstateType;
    numberOfRooms?: number;
    numberOfBedRooms?: number;
    livingSurface: number;
    cityName: string;
    houseNumber?: string;
    postalCode: string;
    streetName: string;
    portal: Portal;
}
const UpdateCard = ({
    estateType,
    numberOfRooms,
    numberOfBedRooms,
    livingSurface,
    cityName,
    houseNumber = '',
    postalCode,
    streetName,
    portal,
}: Props) => {
    const { t } = useTranslation(NAME_SPACE);
    const { setStepAndNavigate } = useStepUtils();
    const dispatch = useAppDispatch();

    const { formValues } = useAppContext();

    const addressName = isFrenchPortal(portal)
        ? `${houseNumber} ${streetName}`.trim()
        : `${streetName} ${houseNumber}`.trim();
    const address = `${addressName}, ${postalCode} ${cityName}`.trim();
    let roomsTranslation = undefined;
    if (numberOfRooms) {
        const suffix = numberOfRooms > 1 ? 'other' : 'one';
        roomsTranslation = t(`step_update.card.numberOfRooms_${suffix}`, {
            replace: {
                numberOfRooms,
            },
        });
    } else if (numberOfBedRooms) {
        const suffix = numberOfBedRooms > 1 ? 'other' : 'one';
        roomsTranslation = t(`step_update.card.numberOfBedRooms_${suffix}`, {
            replace: {
                numberOfBedRooms,
            },
        });
    }

    const handleEditButtonPress = () => {
        dispatch({
            type: EActionType.SetIsFromUpdatePage,
            payload: false,
        });

        // Reset authorContactDetailsId when we go to the funnel in order to pass again
        // the normal phone number validation process.
        dispatch({
            type: EActionType.SetStepValues,
            payload: {
                ...formValues,
                authorContactDetailsId: undefined,
            },
        });

        setStepAndNavigate({ nextStepIndex: 0 });
    };

    return (
        <Card borderRadius="16">
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                textAlign="center"
                padding="spacing.8"
                gap="spacing.16"
            >
                <Box as="span">
                    {estateType === EstateType.House ? (
                        <Icon width="60" height="60" name="house" />
                    ) : (
                        <Icon width="60" height="60" name="apartment" />
                    )}
                </Box>
                <Box display="flex" flexDirection="column" textAlign="left" gap="spacing.16">
                    <Box typography="typography.body.16.bold">{address}</Box>
                    <Box
                        display="flex"
                        gap="spacing.8"
                        justifyContent="start"
                        alignItems="center"
                        typography="typography.body.14.regular"
                    >
                        <Box as="span">
                            {t(
                                estateType === EstateType.House
                                    ? 'common.fields.estateType.house'
                                    : 'common.fields.estateType.apartment',
                            )}
                        </Box>
                        <Box as="span" typography="typography.body.11.regular">
                            •
                        </Box>
                        <Box as="span">{livingSurface} m&sup2;</Box>
                        <Box as="span" typography="typography.body.11.regular">
                            •
                        </Box>
                        {roomsTranslation && <Box as="span">{roomsTranslation}</Box>}
                    </Box>
                    <Box>
                        <Button
                            variant="secondary"
                            size="40"
                            onPress={handleEditButtonPress}
                            testId="editInformation"
                        >
                            {t('step_update.card.button.editInformation')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Card>
    );
};

export default UpdateCard;
